import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { MobileHeaderSlotContext } from '../../organisms/MainPage/MobileNavigation/MobileHeaderSlotContext';
import { useSelector } from 'react-redux';
import {
    UNSAFE_LocationContext as RouterLocationContext,
    UNSAFE_NavigationContext as RouterNavigationContext,
    useInRouterContext,
} from 'react-router-dom';

import OpenStreetMapView from '../../organisms/Map/OpenStreetMapView';
import DiscoverMapPanel from '../../organisms/Discover/DiscoverMapPanel';
import DiscoverMarkerComposerCard from '../../organisms/Discover/DiscoverMarkerComposerCard';
import DiscoverMarkerTablePanel from '../../organisms/Discover/DiscoverMarkerTablePanel';
import { useDiscoverMarkers } from '../../../hooks/useDiscoverMarkers';
import useUserProfileSummary from '../../../hooks/useUserProfileSummary';
import { eventService } from '../../../services/eventService';
import { geocodingClient } from '../../../services/geocoding/geocodingClient';
import {
    resolveTypedAddress,
    TYPED_ADDRESS_RESOLUTION_STATUS,
} from '../../../services/geocoding/addressResolution';
import {
    DEFAULT_BOUNDS_PRECISION,
    isCoordinateWithinBounds,
    normalizeWrappedBounds,
    sanitizeBounds,
    serializeBoundsKey,
} from '../../../utils/mapConfig';
import useLocaleDateFormatter from '../../../hooks/useLocaleDateFormatter';
import useResizableBottomSheet from '../../../hooks/useResizableBottomSheet';
import { geoService } from '../../../services/geoService';
import BottomSheet from '../../molecules/BottomSheet';
import SelectionToolPanel from '../../molecules/Discover/SelectionToolPanel';
import DiscoverOverlayStack from '../../molecules/Discover/DiscoverOverlayStack';
import DiscoverSelectionActionsToolbar from '../../molecules/Discover/DiscoverSelectionActionsToolbar';
import DiscoverMapSearchControl from '../../molecules/Discover/DiscoverMapSearchControl';
import LoadingSpinner from '../../atoms/LoadingSpinner';
import DiscoverLayerWizardModal from '../../organisms/Discover/DiscoverLayerWizardModal';
import ConfirmDialog from '../../molecules/ConfirmDialog';
import ScheduledEntityDetailModal from '../../molecules/ScheduledEntityDetailModal';
import EventCreateModal from '../../molecules/EventCreateModal';
import VenueFormModal from '../../molecules/VenueFormModal';
import useAddressAutocompletePicker from '../../../hooks/useAddressAutocompletePicker';
import {
    compileLayerPredicates,
    applyCompiledLayerFilters,
} from '../../../utils/discoverLayerPredicates';
import { annotateMarkersWithPopupOverrides } from '../../../utils/discoverLayerPopupMapping';
import { readDiscoverEditIntent } from '../../../utils/discoverComposerIntent';
import { isCanonicalObjectId, normalizeScheduledObjectType } from '../../../utils/scheduledEntity';
import {
    DEFAULT_LOCATION_COUNTRY_OPTION,
    buildDisplayFromParts,
    calculateLocationDistanceKm,
    formatLocationDisplay,
    hasConflictingAdministrativeAreas,
    normalizeCountryCode,
    normalizeFirstLevelRegionName,
} from '../../../utils/location';
import { resolveLocationFromCoordinates } from '../../../services/geocoding/locationResolution';
import { normalizeDiscoverPrivacy, toDiscoverPrivacy } from '../../../utils/discoverVisibility';
import { filterPublicDiscoverMarkers } from '../../../utils/discoverMarkerVisibility';
import {
    toDiscoverSearchCameraRequest,
    toDiscoverSearchMarker,
} from '../../../utils/discoverMapSearch';
import { resolveUserDisplayName } from '../../../utils/userIdentity';
import { arrayMove } from '@dnd-kit/sortable';

import './index.scss';

const DEFAULT_MARKER_TYPES = ['ARTIST', 'BAND', 'EVENT', 'SESSION', 'VENUE'];
const COMPOSER_MARKER_TYPES = ['EVENT', 'SESSION', 'VENUE'];
const DISCOVER_FALLBACK_CENTER = Object.freeze({ lat: 39.5, lng: -98.35 });
const DISCOVER_FALLBACK_ZOOM = 4;
const DISCOVER_USER_LOCATION_ZOOM = 10;
const DISCOVER_MARKER_VIEWPORT_LIMIT = 500;
const FALLBACK_DISCOVER_LOCATION = Object.freeze({
    pathname: '',
    search: '',
    state: null,
});
const DISCOVER_FALLBACK_VIEWPORT = Object.freeze({
    center: DISCOVER_FALLBACK_CENTER,
    zoom: DISCOVER_FALLBACK_ZOOM,
});
const LOCATION_COORDINATE_RANGES = Object.freeze({
    lat: Object.freeze({ min: -90, max: 90 }),
    lng: Object.freeze({ min: -180, max: 180 }),
});
const MAX_AUTHOR_LENGTH = 64;
const DATE_INPUT_VALUE_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/;
const LOADING_MAP_TITLE = 'Loading map';
const LOADING_MAP_MESSAGE = 'Fetching nearby music hubs…';
const COMPOSER_LOCATION_REVIEW_MESSAGE = 'Try another map position or choose a more specific address.';
const COMPOSER_LOCATION_MISS_MESSAGE = `We could not automatically determine the address for these coordinates. ${COMPOSER_LOCATION_REVIEW_MESSAGE}`;
const COMPOSER_LOCATION_RESOLUTION_FAILED_MESSAGE = `Unable to resolve the selected coordinates automatically. ${COMPOSER_LOCATION_REVIEW_MESSAGE}`;
const COMPOSER_LOCATION_RESOLVING_SUBMIT_MESSAGE = 'Wait for the selected location to finish resolving before saving.';
const COMPOSER_ADDRESS_RESOLUTION_FAILED_MESSAGE = 'We could not find that address. Choose a suggestion or enter a more specific address.';
const COMPOSER_COARSE_ADDRESS_MATCH_MESSAGE = 'We found only a broader area for that address. Choose a suggestion to confirm it or enter a more specific address.';
const COMPOSER_METADATA_RETENTION_MAX_DISTANCE_KM = 5;
const COMPOSER_AUTO_RESOLVED_LOCATION_SOURCE = 'auto';
const COMPOSER_MANUAL_LOCATION_SOURCE = 'manual';
const COMPOSER_TRACKED_LOCATION_FIELDS = Object.freeze([
    'city',
    'stateOrRegion',
    'country',
    'countryCode',
    'address',
]);
const DEFAULT_COMPOSER_VALUES = Object.freeze({
    name: '',
    type: COMPOSER_MARKER_TYPES[0],
    latitude: DISCOVER_FALLBACK_CENTER.lat.toFixed(5),
    longitude: DISCOVER_FALLBACK_CENTER.lng.toFixed(5),
    city: '',
    stateOrRegion: '',
    country: '',
    countryCode: '',
    address: '',
    description: '',
    startDate: '',
    endDate: '',
    privacy: 'PUBLIC',
});
const SELECTION_PANE_RESIZE_CONFIG = Object.freeze({
    defaultHeightRatio: 0.5,
    minHeightRatio: 0.2,
    maxHeightRatio: 0.85,
    minHeightPx: 220,
    resizeStep: 24,
});

const normalizeMarkerType = (type) => {
    if (!type) {
        return '';
    }
    return String(type).trim().toUpperCase();
};

const sanitizeViewportCenter = (center) => {
    if (!center || typeof center !== 'object') {
        return null;
    }
    const latitude = center.lat ?? center.latitude;
    const longitude = center.lng ?? center.lon ?? center.longitude;
    if (typeof latitude !== 'number' || !Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
        return null;
    }
    if (typeof longitude !== 'number' || !Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
        return null;
    }
    return { lat: latitude, lng: longitude };
};

const isScheduledMarkerType = (type) => (
    type === 'EVENT' || type === 'SESSION'
);

const toScheduledDetailSeed = (marker) => {
    if (!marker || typeof marker !== 'object') {
        return null;
    }
    const objectType = normalizeScheduledObjectType(marker.type);
    const objectId = typeof marker.id === 'string' ? marker.id.trim() : '';
    if (!objectType || !objectId || !isCanonicalObjectId(objectId)) {
        return null;
    }
    const metadata = marker.metadata && typeof marker.metadata === 'object'
        ? marker.metadata
        : {};
    return {
        objectType,
        objectId,
        summary: {
            title: typeof marker.name === 'string' ? marker.name.trim() : '',
            status: typeof metadata.status === 'string' ? metadata.status.trim().toUpperCase() : '',
            startAt: typeof metadata.startDate === 'string' ? metadata.startDate.trim() : '',
            endAt: typeof metadata.endDate === 'string' ? metadata.endDate.trim() : '',
            city: typeof metadata.city === 'string' ? metadata.city.trim() : '',
            visibility: typeof marker.privacy === 'string' ? marker.privacy.trim().toUpperCase() : '',
        },
    };
};

const hslToHex = (hue, saturation, lightness) => {
    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
    const normalizedHue = ((hue % 360) + 360) % 360;
    const normalizedSaturation = clamp(saturation, 0, 100) / 100;
    const normalizedLightness = clamp(lightness, 0, 100) / 100;

    const chroma = (1 - Math.abs(2 * normalizedLightness - 1)) * normalizedSaturation;
    const hueSegment = normalizedHue / 60;
    const secondComponent = chroma * (1 - Math.abs((hueSegment % 2) - 1));
    const lightnessOffset = normalizedLightness - chroma / 2;

    let red = 0;
    let green = 0;
    let blue = 0;

    if (normalizedHue < 60) {
        red = chroma;
        green = secondComponent;
    } else if (normalizedHue < 120) {
        red = secondComponent;
        green = chroma;
    } else if (normalizedHue < 180) {
        green = chroma;
        blue = secondComponent;
    } else if (normalizedHue < 240) {
        green = secondComponent;
        blue = chroma;
    } else if (normalizedHue < 300) {
        red = secondComponent;
        blue = chroma;
    } else {
        red = chroma;
        blue = secondComponent;
    }

    const toHex = (value) => {
        const channel = clamp(Math.round((value + lightnessOffset) * 255), 0, 255);
        return channel.toString(16).padStart(2, '0');
    };

    return `#${toHex(red)}${toHex(green)}${toHex(blue)}`;
};

const hashStringToColor = (input) => {
    if (!input) {
        return '#5f8cff';
    }
    let hash = 0;
    for (let i = 0; i < input.length; i += 1) {
        hash = (hash << 5) - hash + input.charCodeAt(i);
        hash |= 0; // Convert to 32bit integer
    }
    const hue = Math.abs(hash) % 360;
    return hslToHex(hue, 70, 55);
};

const TYPE_COLOR_OVERRIDES = Object.freeze({
    EVENT: '#8b7bff',
    SESSION: '#7bd59b',
    VENUE: '#f5c36b',
    ARTIST: '#d76b6b',
    BAND: '#5fb7c9',
});

const getDefaultTypeColor = (type) => TYPE_COLOR_OVERRIDES[type] ?? hashStringToColor(type);

const HEX_COLOR_PATTERN = /^#([a-f0-9]{6})$/i;

const normalizeHexColor = (value, fallback) => {
    if (typeof value !== 'string') {
        return fallback;
    }
    const trimmed = value.trim();
    if (!trimmed) {
        return fallback;
    }
    const candidate = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    return HEX_COLOR_PATTERN.test(candidate) ? candidate.toLowerCase() : fallback;
};

const sanitizeDisplayName = (value, maxLength = MAX_AUTHOR_LENGTH) => {
    if (typeof value !== 'string') {
        return '';
    }
    const normalized = value
        .replace(/[\r\n\t]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    if (!normalized) {
        return '';
    }
    // Avoid leaking emails/phone numbers/ids
    if (/.+@.+\..+/.test(normalized)) {
        return '';
    }
    const digits = normalized.replace(/[^\d]/g, '');
    if (digits.length >= 7) {
        return '';
    }
    const looksUuidLike = /^[0-9a-f-]{16,}$/i.test(normalized);
    if (looksUuidLike) {
        return '';
    }
    return normalized.slice(0, maxLength);
};

const resolveAuthorName = (user) => {
    if (!user || typeof user !== 'object') {
        return '';
    }
    return sanitizeDisplayName(resolveUserDisplayName(user));
};

const readMetadataValue = (metadata, key) => {
    if (!metadata || typeof metadata !== 'object') {
        return '';
    }
    const value = metadata[key];
    if (value == null) {
        return '';
    }
    return String(value).trim();
};

const readComposerLocationValue = (value) => {
    if (typeof value !== 'string') {
        return '';
    }
    return value.trim();
};

const createComposerLocationFieldSources = () => (
    COMPOSER_TRACKED_LOCATION_FIELDS.reduce((sources, field) => {
        sources[field] = null;
        return sources;
    }, {})
);

const resolveComposerLocationSource = (value, source) => (
    readComposerLocationValue(value) ? source : null
);

const buildManualComposerLocationFieldSources = (values) => ({
    city: resolveComposerLocationSource(values?.city, COMPOSER_MANUAL_LOCATION_SOURCE),
    stateOrRegion: resolveComposerLocationSource(values?.stateOrRegion, COMPOSER_MANUAL_LOCATION_SOURCE),
    country: resolveComposerLocationSource(values?.country, COMPOSER_MANUAL_LOCATION_SOURCE),
    countryCode: resolveComposerLocationSource(values?.countryCode, COMPOSER_MANUAL_LOCATION_SOURCE),
    address: resolveComposerLocationSource(values?.address, COMPOSER_MANUAL_LOCATION_SOURCE),
});

const canRetainMissingComposerLocationFields = (currentValues, location) => (
    calculateLocationDistanceKm(currentValues, location) <= COMPOSER_METADATA_RETENTION_MAX_DISTANCE_KM
    && !hasConflictingAdministrativeAreas(currentValues, location)
);

const buildComposerLocationRetentionBaseline = (values = {}) => ({
    latitude: values.latitude,
    longitude: values.longitude,
    city: readComposerLocationValue(values.city ?? values.cityName),
    stateOrRegion: readComposerLocationValue(values.stateOrRegion ?? values.regionName),
    country: readComposerLocationValue(values.country ?? values.countryName),
    countryCode: normalizeCountryCode(values.countryCode || ''),
});

const mergeComposerResolvedLocationField = ({
    resolvedValue,
    currentValue,
    currentSource,
    retainCurrentValue,
}) => {
    const normalizedResolvedValue = readComposerLocationValue(resolvedValue);
    if (normalizedResolvedValue) {
        return {
            value: normalizedResolvedValue,
            source: COMPOSER_AUTO_RESOLVED_LOCATION_SOURCE,
            retained: false,
        };
    }

    if (!retainCurrentValue) {
        return {
            value: '',
            source: null,
            retained: false,
        };
    }

    const normalizedCurrentValue = readComposerLocationValue(currentValue);
    return {
        value: currentValue,
        source: normalizedCurrentValue ? currentSource : null,
        retained: Boolean(normalizedCurrentValue),
    };
};

const buildComposerGeocodedLocationUpdate = ({
    location,
    currentValues,
    currentSources,
    retentionBaselineValues = currentValues,
}) => {
    const nextAddress = location.fullAddress
        || location.label
        || buildDisplayFromParts({
            city: location.cityName,
            stateOrRegion: location.regionName,
            country: location.countryName,
        });
    const resolvedCountryCode = normalizeCountryCode(location.countryCode || '');
    const retainMissingValues = canRetainMissingComposerLocationFields(retentionBaselineValues, location);
    const nextValues = {};
    let hasRetainedFields = false;
    const nextSources = {
        ...createComposerLocationFieldSources(),
        ...currentSources,
    };

    const resolvedFieldValues = {
        city: location.cityName,
        stateOrRegion: normalizeFirstLevelRegionName(location.regionName),
        country: location.countryName,
        countryCode: resolvedCountryCode,
        address: nextAddress,
    };

    COMPOSER_TRACKED_LOCATION_FIELDS.forEach((field) => {
        const nextField = mergeComposerResolvedLocationField({
            resolvedValue: resolvedFieldValues[field],
            currentValue: currentValues[field],
            currentSource: currentSources?.[field] || null,
            retainCurrentValue: retainMissingValues,
        });
        nextValues[field] = nextField.value;
        nextSources[field] = nextField.source;
        if (nextField.retained) {
            hasRetainedFields = true;
        }
    });

    return {
        values: nextValues,
        sources: nextSources,
        hasRetainedFields,
        addressInputValue: nextValues.address,
    };
};

const normalizeDateInputValue = (value) => {
    if (typeof value !== 'string') {
        return '';
    }
    const trimmed = value.trim();
    if (!trimmed) {
        return '';
    }
    if (DATE_INPUT_VALUE_REGEX.test(trimmed)) {
        return trimmed.slice(0, 16);
    }
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
        return '';
    }
    const pad = (segment) => String(segment).padStart(2, '0');
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};

const parseCoordinate = (value, range) => {
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) {
            return null;
        }
        return value >= range.min && value <= range.max ? value : null;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
            return null;
        }
        const parsed = Number.parseFloat(trimmed);
        if (!Number.isFinite(parsed)) {
            return null;
        }
        return parsed >= range.min && parsed <= range.max ? parsed : null;
    }

    return null;
};

const formatComposerCoordinate = (value) => (
    typeof value === 'number' && Number.isFinite(value) ? value.toFixed(5) : ''
);

const getUserLocationPayload = (user) => (
    user?.profile?.locationDetails
    || user?.locationDetails
    || user?.profile?.location
    || user?.location
    || null
);

const getUserLocationDisplay = (user) => {
    const location = getUserLocationPayload(user);
    if (typeof location === 'string') {
        return location.trim();
    }
    return formatLocationDisplay(
        location,
        user?.profile?.locationDisplay || user?.locationDisplay || '',
    );
};

const getUserLocationCountryCode = (user) => {
    const location = getUserLocationPayload(user);
    if (!location || typeof location !== 'object') {
        return DEFAULT_LOCATION_COUNTRY_OPTION.code;
    }
    return normalizeCountryCode(location.countryCode || '') || DEFAULT_LOCATION_COUNTRY_OPTION.code;
};

const getUserLocationCoordinates = (user) => {
    const location = getUserLocationPayload(user);
    if (!location || typeof location !== 'object') {
        return null;
    }

    const lat = parseCoordinate(location.latitude ?? location.lat, LOCATION_COORDINATE_RANGES.lat);
    const lng = parseCoordinate(location.longitude ?? location.lng ?? location.lon, LOCATION_COORDINATE_RANGES.lng);

    if (lat == null || lng == null) {
        return null;
    }

    return { lat, lng };
};

const hasUserLocation = (user) => Boolean(
    getUserLocationCoordinates(user) || getUserLocationDisplay(user)
);

const normalizeProfileOwnerId = (value) => {
    if (typeof value !== 'string') {
        return '';
    }
    return value.trim().toLowerCase();
};

const readProfileOwnerId = (profile) => normalizeProfileOwnerId(
    profile?.userId
    || profile?.id
    || profile?.user?.id
);

const isOwnerProfileForCurrentUser = (ownerProfile, currentUser) => {
    const currentUserId = normalizeProfileOwnerId(currentUser?.id || currentUser?.userId);
    if (!currentUserId) {
        return false;
    }
    return readProfileOwnerId(ownerProfile) === currentUserId;
};

const resolveDiscoverLocationSource = (ownerProfile, currentUser) => (
    isOwnerProfileForCurrentUser(ownerProfile, currentUser) && hasUserLocation(ownerProfile)
        ? ownerProfile
        : currentUser
);

const toProfileAddressViewport = (geocoded) => {
    const lat = parseCoordinate(geocoded?.latitude, LOCATION_COORDINATE_RANGES.lat);
    const lng = parseCoordinate(geocoded?.longitude, LOCATION_COORDINATE_RANGES.lng);

    if (lat == null || lng == null) {
        return null;
    }

    return {
        center: { lat, lng },
        zoom: DISCOVER_USER_LOCATION_ZOOM,
    };
};

const resolveDiscoverViewport = (locationSource, profileAddressViewport, locationSignature) => {
    const locationCenter = getUserLocationCoordinates(locationSource);
    if (locationCenter) {
        return {
            center: locationCenter,
            zoom: DISCOVER_USER_LOCATION_ZOOM,
        };
    }

    if (
        profileAddressViewport?.signature === locationSignature
        && profileAddressViewport?.viewport
    ) {
        return profileAddressViewport.viewport;
    }

    return DISCOVER_FALLBACK_VIEWPORT;
};

const buildLocationSignature = (user) => {
    if (!user || typeof user !== 'object') {
        return 'anon';
    }
    const location = getUserLocationPayload(user);
    const coordinates = getUserLocationCoordinates(user);
    const formatted = getUserLocationDisplay(user)
        || (typeof location === 'object' ? location?.formattedAddress || '' : '');
    const updatedAt = user?.updatedAt || user?.profile?.updatedAt || '';
    if (!coordinates) {
        return `no-location|${formatted}|${updatedAt}`;
    }
    return `${coordinates.lat}|${coordinates.lng}|${formatted}|${updatedAt}`;
};

function MapStatusBanner({
    variant,
    title,
    message = '',
    actionLabel = undefined,
    onAction = undefined,
    showSpinner = false,
}) {
    return (
        <div className={`discover-status-banner discover-status-banner--${variant}`}>
            <div className="discover-status-banner__body">
                <div className="discover-status-banner__title-row">
                    <p className="discover-status-banner__title">{title}</p>
                    {showSpinner && (
                        <div className="discover-status-banner__spinner" aria-hidden="true">
                            <LoadingSpinner />
                        </div>
                    )}
                </div>
                {message && (
                    <p className="discover-status-banner__message">{message}</p>
                )}
            </div>
            {actionLabel && typeof onAction === 'function' && (
                <button
                    type="button"
                    className="discover-status-banner__action"
                    onClick={onAction}
                >
                    {actionLabel}
                </button>
            )}
        </div>
    );
}

MapStatusBanner.propTypes = {
    variant: PropTypes.oneOf(['info', 'error']).isRequired,
    title: PropTypes.string.isRequired,
    message: PropTypes.string,
    actionLabel: PropTypes.string,
    onAction: PropTypes.func,
    showSpinner: PropTypes.bool,
};

function LoadingMapStatusBanner() {
    return (
        <MapStatusBanner
            variant="info"
            title={LOADING_MAP_TITLE}
            message={LOADING_MAP_MESSAGE}
            showSpinner
        />
    );
}

function MapUnavailableStatusBanner({ onRetry }) {
    return (
        <MapStatusBanner
            variant="error"
            title="Map data unavailable"
            message="We hit a snag fetching locations. Try again in a moment."
            actionLabel="Retry"
            onAction={onRetry}
        />
    );
}

MapUnavailableStatusBanner.propTypes = {
    onRetry: PropTypes.func.isRequired,
};

function normalizeTypeForSelect(type, allowedTypes) {
    const normalized = normalizeMarkerType(type);
    if (!normalized) {
        return allowedTypes[0] ?? DEFAULT_MARKER_TYPES[0];
    }
    if (allowedTypes.includes(normalized)) {
        return normalized;
    }
    return allowedTypes[0] ?? DEFAULT_MARKER_TYPES[0];
}

function normalizePrivacy(value) {
    return normalizeDiscoverPrivacy(value);
}

function toComposerEditMarker(entity) {
    if (!entity || typeof entity !== 'object') {
        return null;
    }

    const objectType = normalizeMarkerType(entity.objectType);
    if (!COMPOSER_MARKER_TYPES.includes(objectType)) {
        return null;
    }

    const location = entity.location && typeof entity.location === 'object'
        ? entity.location
        : {};
    const latitude = Number.isFinite(location.latitude) ? location.latitude : DISCOVER_FALLBACK_CENTER.lat;
    const longitude = Number.isFinite(location.longitude) ? location.longitude : DISCOVER_FALLBACK_CENTER.lng;
    const name = objectType === 'VENUE' ? entity.name : entity.title;

    return {
        id: typeof entity.id === 'string' ? entity.id.trim() : '',
        type: objectType,
        originalType: objectType,
        name: typeof name === 'string' ? name.trim() : '',
        coordinate: {
            lat: latitude,
            lng: longitude,
        },
        metadata: {
            city: typeof location.city === 'string' ? location.city : '',
            stateOrRegion: typeof location.stateOrRegion === 'string' ? location.stateOrRegion : '',
            country: typeof location.country === 'string' ? location.country : '',
            countryCode: typeof location.countryCode === 'string' ? location.countryCode : '',
            address: typeof location.addressLine === 'string' ? location.addressLine : '',
            description: typeof entity.description === 'string' ? entity.description : '',
            startDate: entity.startAt || '',
            endDate: entity.endAt || '',
            status: entity.status || '',
            timezone: typeof entity.timezone === 'string' ? entity.timezone : '',
            location: typeof location.locationName === 'string' ? location.locationName : '',
            organizerObjectType: entity.organizer?.objectType || '',
            organizerObjectId: entity.organizer?.objectId || '',
            venueObjectId: entity.venue?.objectId || '',
            joinPolicy: entity.joinPolicy || '',
            attendeeLimit: entity.attendeeLimit ?? '',
            website: entity.website || '',
            phone: entity.phone || '',
            capacity: entity.capacity ?? '',
            amenities: Array.isArray(entity.amenities) ? entity.amenities : undefined,
        },
        privacy: toDiscoverPrivacy(entity.visibility),
        createdBy: entity.ownerId,
        createdAt: entity.createdAt,
        updatedAt: entity.updatedAt,
    };
}

function useOptionalRouterLocation() {
    const inRouterContext = useInRouterContext();
    const locationContext = useContext(RouterLocationContext);

    if (!inRouterContext || !locationContext?.location) {
        return FALLBACK_DISCOVER_LOCATION;
    }

    return locationContext.location;
}

function useOptionalRouterNavigate() {
    const inRouterContext = useInRouterContext();
    const navigationContext = useContext(RouterNavigationContext);

    return useCallback((to, options = {}) => {
        if (!inRouterContext || !navigationContext?.navigator) {
            return;
        }

        const navigator = navigationContext.navigator;
        if (typeof to === 'number') {
            navigator.go?.(to);
            return;
        }

        const navigateFn = options.replace ? navigator.replace : navigator.push;
        navigateFn.call(navigator, to, options.state, options);
    }, [inRouterContext, navigationContext]);
}

export default function DiscoverPage() {
    const { t } = useTranslation();
    // On mobile the header exposes a slot; relocate the search + selection/table
    // toolbar into it. Null on desktop → render in place (unchanged).
    const mobileHeaderSlot = useContext(MobileHeaderSlotContext);
    const location = useOptionalRouterLocation();
    const navigate = useOptionalRouterNavigate();
    const { formatDateTime } = useLocaleDateFormatter();
    const currentUser = useSelector((state) => state?.auth?.user);
    const authorName = useMemo(() => resolveAuthorName(currentUser), [currentUser]);
    const locale = useMemo(() => (typeof navigator !== 'undefined' ? navigator.language : 'en'), []);
    const discoverEditIntent = useMemo(() => readDiscoverEditIntent(location.state), [location.state]);
    const { profile: ownerProfileSummary } = useUserProfileSummary(currentUser?.id, { source: 'owner' });
    const discoverLocationSource = useMemo(
        () => resolveDiscoverLocationSource(ownerProfileSummary, currentUser),
        [currentUser, ownerProfileSummary],
    );
    const locationSignature = useMemo(
        () => buildLocationSignature(discoverLocationSource),
        [discoverLocationSource],
    );
    const profileLocationCenter = useMemo(
        () => getUserLocationCoordinates(discoverLocationSource),
        [discoverLocationSource],
    );
    const profileLocationQuery = useMemo(
        () => (profileLocationCenter ? '' : getUserLocationDisplay(discoverLocationSource)),
        [discoverLocationSource, profileLocationCenter],
    );
    const profileLocationCountryCode = useMemo(
        () => getUserLocationCountryCode(discoverLocationSource),
        [discoverLocationSource],
    );
    const [profileAddressViewport, setProfileAddressViewport] = useState(null);
    const discoverViewport = useMemo(
        () => resolveDiscoverViewport(discoverLocationSource, profileAddressViewport, locationSignature),
        [discoverLocationSource, locationSignature, profileAddressViewport],
    );
    const [pendingEditIntent, setPendingEditIntent] = useState(null);
    const editIntentRequestIdRef = useRef(0);
    const [viewportBounds, setViewportBounds] = useState(null);
    const [viewportCenter, setViewportCenter] = useState(null);
    const [viewportKey, setViewportKey] = useState(null);
    const viewportKeyRef = useRef(null);
    const mapContainerRef = useRef(null);
    const mountedRef = useRef(true);
    const profileAddressLookupRequestIdRef = useRef(0);

    useEffect(() => () => {
        mountedRef.current = false;
    }, []);

    useEffect(() => {
        profileAddressLookupRequestIdRef.current += 1;
        const requestId = profileAddressLookupRequestIdRef.current;

        if (profileLocationCenter || !profileLocationQuery) {
            setProfileAddressViewport(null);
            return undefined;
        }

        const abortController = typeof AbortController === 'function'
            ? new AbortController()
            : null;

        geocodingClient.forwardLookup({
            text: profileLocationQuery,
            countryCode: profileLocationCountryCode,
            limit: 1,
        }, abortController?.signal)
            .then((suggestions) => {
                if (!mountedRef.current || profileAddressLookupRequestIdRef.current !== requestId) {
                    return;
                }
                const geocoded = geocodingClient.toGeocodedLocation(suggestions?.[0]);
                const viewport = toProfileAddressViewport(geocoded);
                setProfileAddressViewport(viewport ? {
                    signature: locationSignature,
                    viewport,
                } : null);
            })
            .catch((error) => {
                if (!mountedRef.current || profileAddressLookupRequestIdRef.current !== requestId) {
                    return;
                }
                if (error?.name === 'AbortError') {
                    return;
                }
                setProfileAddressViewport(null);
            });

        return () => {
            abortController?.abort?.();
        };
    }, [
        locationSignature,
        profileLocationCenter,
        profileLocationCountryCode,
        profileLocationQuery,
    ]);

    const handleViewportChange = useCallback((viewport) => {
        const nextBounds = sanitizeBounds(normalizeWrappedBounds(viewport?.bounds));
        const nextCenter = sanitizeViewportCenter(viewport?.center);
        const nextKey = serializeBoundsKey(nextBounds, DEFAULT_BOUNDS_PRECISION);
        if (viewportKeyRef.current === nextKey) {
            setViewportCenter(nextCenter);
            return;
        }
        viewportKeyRef.current = nextKey;
        setViewportBounds(nextBounds);
        setViewportCenter(nextCenter);
        setViewportKey(nextKey);
    }, []);

    const [layers, setLayers] = useState([]);
    const [layersLoaded, setLayersLoaded] = useState(false);
    const activeMarkerTypes = useMemo(() => {
        if (!layersLoaded) {
            return undefined;
        }
        return Array.from(new Set(layers
            .filter((layer) => layer.enabled)
            .map((layer) => normalizeMarkerType(layer.objectType))
            .filter(Boolean)))
            .sort();
    }, [layers, layersLoaded]);
    const hasActiveMarkerLayerScope = activeMarkerTypes === undefined || activeMarkerTypes.length > 0;

    const {
        markers,
        isLoading,
        error,
        isStale,
        refresh,
        createMarker,
        registerCreatedEntity,
        updateMarker,
        lastUpdated,
        markerDataKey,
    } = useDiscoverMarkers({
        invalidateKey: locationSignature,
        bounds: viewportBounds,
        center: viewportCenter,
        limit: DISCOVER_MARKER_VIEWPORT_LIMIT,
        types: activeMarkerTypes,
        requireBounds: true,
    });

    const markerCount = markers.length;
    const preserveOpenPopupOnDataGap = Boolean(
        viewportKey
        && viewportKey !== markerDataKey
        && hasActiveMarkerLayerScope
        && (isLoading || error),
    );
    const [isComposerOpen, setIsComposerOpen] = useState(false);
    const [composerMapAddress, setComposerMapAddress] = useState(null);
    const [composerValues, setComposerValues] = useState(() => ({
        ...DEFAULT_COMPOSER_VALUES,
    }));
    const composerValuesRef = useRef({
        ...DEFAULT_COMPOSER_VALUES,
    });
    const [editingMarker, setEditingMarker] = useState(null);
    const [composerError, setComposerError] = useState('');
    const [editIntentError, setEditIntentError] = useState('');
    const [composerLocationError, setComposerLocationError] = useState('');
    const [isComposerSubmitting, setIsComposerSubmitting] = useState(false);
    const [isComposerLocationResolving, setIsComposerLocationResolving] = useState(false);
    const [creationNotice, setCreationNotice] = useState(null);
    const [scheduledDetailTarget, setScheduledDetailTarget] = useState(null);
    const [isLayerWizardOpen, setIsLayerWizardOpen] = useState(false);
    const [editingLayer, setEditingLayer] = useState(null);
    const [deletingLayerId, setDeletingLayerId] = useState(null);
    const [selectionMode, setSelectionMode] = useState('none');
    const [selectedMarkerIds, setSelectedMarkerIds] = useState([]);
    const [selectedMarkers, setSelectedMarkers] = useState([]);
    const [isTableOpen, setIsTableOpen] = useState(false);
    const [isTableLoading, setIsTableLoading] = useState(false);
    const [tableError, setTableError] = useState('');
    const [searchAddressValue, setSearchAddressValue] = useState('');
    const [searchCameraRequest, setSearchCameraRequest] = useState(null);
    const [searchResultMarker, setSearchResultMarker] = useState(null);
    const [refreshingCount, setRefreshingCount] = useState(0);
    const isEditing = Boolean(editingMarker);
    const isManualRefreshInFlight = refreshingCount > 0;
    const isSelectionPaneOpen = isTableOpen;
    const composerLocationRequestIdRef = useRef(0);
    const composerLocationFieldSourcesRef = useRef(createComposerLocationFieldSources());
    const composerLocationRetentionBaselineRef = useRef(null);
    const composerAddressNeedsResolutionRef = useRef(false);
    const composerIsOpenRef = useRef(isComposerOpen);
    const searchRequestSequenceRef = useRef(0);
    const selectedSearchAddressRef = useRef('');
    // Forward-declared so the picker hook's `onSelect` (built at init) can
    // reach `applyComposerGeocodedLocation`, defined further down, without
    // re-entering the hook on every redefinition.
    const applyComposerGeocodedLocationRef = useRef(() => {});
    const handleComposerPickerSelect = useCallback((geocoded) => {
        applyComposerGeocodedLocationRef.current(geocoded);
    }, []);

    const handleSearchAddressChange = useCallback((nextValue) => {
        const normalizedValue = typeof nextValue === 'string' ? nextValue : '';
        setSearchAddressValue(normalizedValue);
        if (!normalizedValue.trim() || normalizedValue.trim() !== selectedSearchAddressRef.current) {
            selectedSearchAddressRef.current = '';
            setSearchResultMarker(null);
        }
    }, []);

    const handleSearchAddressSelect = useCallback((geocoded, suggestion) => {
        if (!geocoded) {
            return;
        }

        searchRequestSequenceRef.current += 1;
        const requestId = `discover-search-${searchRequestSequenceRef.current}`;
        const cameraRequest = toDiscoverSearchCameraRequest({
            geocoded,
            suggestion,
            requestId,
        });
        const marker = toDiscoverSearchMarker({
            geocoded,
            requestId,
        });

        if (!cameraRequest || !marker) {
            return;
        }

        const label = marker.label || geocoded.label || geocoded.fullAddress || '';
        selectedSearchAddressRef.current = label.trim();
        setSearchAddressValue(label);
        setSearchCameraRequest(cameraRequest);
        setSearchResultMarker(marker);
    }, []);

    useEffect(() => {
        composerIsOpenRef.current = isComposerOpen;
    }, [isComposerOpen]);

    useEffect(() => {
        composerValuesRef.current = composerValues;
    }, [composerValues]);

    const setComposerValuesAndTrack = useCallback((nextValues) => {
        composerValuesRef.current = nextValues;
        setComposerValues(nextValues);
    }, []);

    const cancelPendingEditIntent = useCallback(() => {
        editIntentRequestIdRef.current += 1;
        setPendingEditIntent(null);
    }, []);

    const isComposerAutocompleteEnabled = isComposerOpen
        && (isEditing || composerValues.type === 'SESSION')
        && !isComposerSubmitting
        && !isComposerLocationResolving;
    const {
        containerRef: composerAddressFieldRef,
        inputValue: composerAddressValue,
        debouncedValue: composerAddressDebouncedValue,
        suggestions: composerAddressSuggestions,
        isLoading: isComposerAddressLoading,
        error: composerAddressLookupError,
        isOpen: isComposerAddressOpen,
        highlightedIndex: composerAddressHighlightedIndex,
        setInputValue: setComposerAddressValue,
        setHighlightedIndex: setComposerAddressHighlightedIndex,
        openDropdown: openComposerAddressDropdown,
        closeDropdown: closeComposerAddressDropdown,
        pickSuggestion: handleComposerAddressSuggestionSelect,
        handleKeyDown: handleComposerAddressKeyDown,
        handleBlur: handleComposerAddressFieldBlur,
    } = useAddressAutocompletePicker({
        minChars: 3,
        debounceMs: 300,
        countryCode: normalizeCountryCode(composerValues.countryCode) || undefined,
        limit: 8,
        enabled: isComposerAutocompleteEnabled,
        onSelect: handleComposerPickerSelect,
    });

    const {
        height: selectionPaneHeight,
        isResizing: isSelectionPaneResizing,
        startResize: startSelectionPaneResize,
        handleKeyDown: handleSelectionPaneResizerKeyDown,
    } = useResizableBottomSheet({
        ...SELECTION_PANE_RESIZE_CONFIG,
        containerRef: mapContainerRef,
    });

    const markerTypes = useMemo(() => {
        const base = [...DEFAULT_MARKER_TYPES];
        const fromData = markers.reduce((acc, marker) => {
            const markerType = normalizeMarkerType(marker.type);
            if (markerType && !acc.includes(markerType)) {
                acc.push(markerType);
            }
            return acc;
        }, []);
        const merged = [...base];
        fromData.forEach((type) => {
            if (!merged.includes(type)) {
                merged.push(type);
            }
        });
        return merged;
    }, [markers]);

    const composerMarkerTypes = useMemo(
        () => markerTypes.filter((type) => COMPOSER_MARKER_TYPES.includes(type)),
        [markerTypes],
    );

    const layersLoadPromiseRef = useRef(null);
    const layerMutationVersionRef = useRef(0);
    const layerRetryRef = useRef(0);
    const layerRetryTimerRef = useRef(null);
    const selectedMarkersBucketRef = useRef([]);
    const layerToggleTimerRef = useRef(null);
    const pendingLayerTogglesRef = useRef({});
    const optimisticLayerTogglesRef = useRef({});
    const optimisticLayerToggleBaselinesRef = useRef({});
    const layerReloadRequestRef = useRef(() => {});

    const applyOptimisticLayerToggles = useCallback((nextLayers) => {
        const overrides = optimisticLayerTogglesRef.current || {};
        const overrideIds = Object.keys(overrides);
        if (!overrideIds.length) {
            return nextLayers;
        }
        let changed = false;
        const resolved = nextLayers.map((layer) => {
            if (!Object.prototype.hasOwnProperty.call(overrides, layer.id)) {
                return layer;
            }
            const enabled = overrides[layer.id];
            if (layer.enabled === enabled) {
                return layer;
            }
            changed = true;
            return { ...layer, enabled };
        });
        return changed ? resolved : nextLayers;
    }, []);

    const clearOptimisticLayerToggle = useCallback((layerId, enabled) => {
        const overrides = optimisticLayerTogglesRef.current;
        if (!Object.prototype.hasOwnProperty.call(overrides, layerId) || overrides[layerId] !== enabled) {
            return false;
        }
        delete overrides[layerId];
        delete optimisticLayerToggleBaselinesRef.current[layerId];
        return true;
    }, []);

    const rollbackOptimisticLayerToggle = useCallback((layerId, enabled) => {
        const overrides = optimisticLayerTogglesRef.current;
        if (!Object.prototype.hasOwnProperty.call(overrides, layerId) || overrides[layerId] !== enabled) {
            return false;
        }
        delete overrides[layerId];
        const baselines = optimisticLayerToggleBaselinesRef.current;
        const hasBaseline = Object.prototype.hasOwnProperty.call(baselines, layerId);
        const rollbackEnabled = hasBaseline ? baselines[layerId] : !enabled;
        delete baselines[layerId];
        layerMutationVersionRef.current += 1;
        setLayers((prev) => prev.map((layer) => (
            layer.id === layerId ? { ...layer, enabled: rollbackEnabled } : layer
        )));
        return true;
    }, []);

    const persistLayerEnabled = useCallback((layerId, enabled, errorMessage) => {
        geoService.updateLayer(layerId, { enabled })
            .then(() => clearOptimisticLayerToggle(layerId, enabled))
            .catch((err) => {
                console.error(errorMessage, err);
                if (rollbackOptimisticLayerToggle(layerId, enabled)) {
                    layerReloadRequestRef.current();
                }
            });
    }, [clearOptimisticLayerToggle, rollbackOptimisticLayerToggle]);

    const setLayerEnabledOptimistically = useCallback((layerId, enabled) => {
        optimisticLayerTogglesRef.current[layerId] = enabled;
        layerMutationVersionRef.current += 1;
        setLayers((prev) => prev.map((layer) => {
            if (layer.id !== layerId) {
                return layer;
            }
            if (!Object.prototype.hasOwnProperty.call(optimisticLayerToggleBaselinesRef.current, layerId)) {
                optimisticLayerToggleBaselinesRef.current[layerId] = layer.enabled;
            }
            return { ...layer, enabled };
        }));
    }, []);

    const loadLayers = useCallback(() => {
        if (layersLoadPromiseRef.current) {
            return layersLoadPromiseRef.current;
        }
        const versionAtStart = layerMutationVersionRef.current;
        const loadPromise = (async () => {
            try {
                const payload = await geoService.fetchLayers();
                const entries = Array.isArray(payload) ? payload : [];

                const hydrated = entries
                    .filter((entry) => entry && entry.id)
                    .map((entry) => ({
                        id: entry.id,
                        name: entry.name || '',
                        objectType: normalizeMarkerType(entry.type || entry.objectType),
                        filters: entry.filters || null,
                        popup: entry.popup || null,
                        displayOrder: entry.displayOrder ?? null,
                        color: normalizeHexColor(
                            entry.color,
                            getDefaultTypeColor(normalizeMarkerType(entry.type || entry.objectType)),
                        ),
                        enabled: typeof entry.enabled === 'boolean' ? entry.enabled : true,
                        createdAt: entry.createdAt || null,
                    }));
                const resolvedLayers = applyOptimisticLayerToggles(hydrated);
                if (layerMutationVersionRef.current === versionAtStart) {
                    setLayers(resolvedLayers);
                    setLayersLoaded(true);
                    layerRetryRef.current = 0;
                    if (layerRetryTimerRef.current) {
                        clearTimeout(layerRetryTimerRef.current);
                        layerRetryTimerRef.current = null;
                    }
                    return resolvedLayers;
                }
                // Stale response — re-fetch scheduled in finally block.
                // Return null so callers don't act on outdated data.
                return null;
            } catch (fetchError) {
                console.error('Failed to load map layers', fetchError);
                // Schedule a retry so a transient failure doesn't leave Discover
                // blank for the rest of the session. Exponential backoff caps at 30s.
                const attempt = (layerRetryRef.current = (layerRetryRef.current || 0) + 1);
                const delay = Math.min(1000 * 2 ** (attempt - 1), 30000);
                layerRetryTimerRef.current = setTimeout(() => {
                    layerRetryTimerRef.current = null;
                    loadLayers();
                }, delay);
                return null;
            }
        })();
        layersLoadPromiseRef.current = loadPromise;
        loadPromise.finally(() => {
            layersLoadPromiseRef.current = null;
            if (layerMutationVersionRef.current !== versionAtStart) {
                loadLayers();
            }
        });
        return loadPromise;
    }, [applyOptimisticLayerToggles]);

    useEffect(() => {
        layerReloadRequestRef.current = loadLayers;
    }, [loadLayers]);

    useEffect(() => {
        loadLayers();
    }, [loadLayers]);

    const visibleMarkers = useMemo(
        () => filterPublicDiscoverMarkers(markers),
        [markers],
    );

    /** Pre-compile layer predicates (only recomputed when layers change). */
    const compiledLayerPredicates = useMemo(
        () => compileLayerPredicates(layers),
        [layers],
    );

    /** Apply pre-compiled predicates to markers (no system-type gate).
     *  Before layers load, pass through all markers so a slow fetch doesn't blank the map. */
    const { filtered: filteredMarkers, layerMatchCounts: layerCounts } = useMemo(
        () => layersLoaded
            ? applyCompiledLayerFilters(visibleMarkers, compiledLayerPredicates)
            : { filtered: visibleMarkers, layerMatchCounts: {} },
        [visibleMarkers, compiledLayerPredicates, layersLoaded],
    );
    const markersWithPopupOverrides = useMemo(
        () => annotateMarkersWithPopupOverrides(filteredMarkers, layers),
        [filteredMarkers, layers],
    );
    const visibleMarkerCount = filteredMarkers.length;
    const viewportVisibleMarkerCount = useMemo(() => {
        if (!viewportBounds) {
            return visibleMarkerCount;
        }
        return filteredMarkers.reduce((count, marker) => (
            isCoordinateWithinBounds(marker.coordinate, viewportBounds) ? count + 1 : count
        ), 0);
    }, [filteredMarkers, viewportBounds, visibleMarkerCount]);
    const hasOffscreenVisibleMarkers = visibleMarkerCount > 0 && viewportVisibleMarkerCount === 0;

    /** Derive markerColors from the first enabled layer per type. */
    const markerColors = useMemo(() => {
        const colors = {};
        for (const layer of layers) {
            const type = layer.objectType;
            if (type && layer.enabled && !colors[type]) {
                colors[type] = layer.color || getDefaultTypeColor(type);
            }
        }
        DEFAULT_MARKER_TYPES.forEach((type) => {
            if (!colors[type]) {
                colors[type] = getDefaultTypeColor(type);
            }
        });
        return colors;
    }, [layers]);

    useEffect(() => {
        if (!selectedMarkerIds.length) {
            selectedMarkersBucketRef.current = [];
            setSelectedMarkers([]);
            return;
        }

        const markerIndex = new Map(filteredMarkers.map((marker) => [marker.id, marker]));
        const nextIds = selectedMarkerIds.filter((id) => markerIndex.has(id));
        const nextMarkers = nextIds.map((id) => markerIndex.get(id));

        if (nextIds.length !== selectedMarkerIds.length) {
            setSelectedMarkerIds(nextIds);
        }
        setSelectedMarkers(nextMarkers);
        selectedMarkersBucketRef.current = nextMarkers;
    }, [filteredMarkers, selectedMarkerIds]);

    const selectedCount = selectedMarkers.length;
    const handleManualRefresh = useCallback(() => {
        if (!hasActiveMarkerLayerScope) {
            return;
        }
        setRefreshingCount((count) => count + 1);
        const refreshPromise = refresh();
        if (refreshPromise && typeof refreshPromise.finally === 'function') {
            refreshPromise
                .catch((err) => {
                    console.error('Failed to refresh map markers:', err);
                })
                .finally(() => {
                    setRefreshingCount((count) => Math.max(0, count - 1));
                });
            return;
        }
        setRefreshingCount((count) => Math.max(0, count - 1));
    }, [hasActiveMarkerLayerScope, refresh]);

    const statusBanner = useMemo(() => {
        if (!hasActiveMarkerLayerScope) {
            return null;
        }

        if (isManualRefreshInFlight) {
            return <LoadingMapStatusBanner />;
        }

        if (error && (markerCount === 0 || visibleMarkerCount > 0)) {
            return <MapUnavailableStatusBanner onRetry={handleManualRefresh} />;
        }

        if (isLoading && (
            markerCount === 0
            || hasOffscreenVisibleMarkers
            || (viewportVisibleMarkerCount > 0 && isStale)
        )) {
            return <LoadingMapStatusBanner />;
        }

        return null;
    }, [
        error,
        handleManualRefresh,
        hasActiveMarkerLayerScope,
        hasOffscreenVisibleMarkers,
        isLoading,
        isManualRefreshInFlight,
        isStale,
        markerCount,
        visibleMarkerCount,
        viewportVisibleMarkerCount,
    ]);

    const loadingLabel = useMemo(() => {
        if (isLoading && markerCount > 0) {
            return 'Updating map data…';
        }
        return 'Loading map experience…';
    }, [isLoading, markerCount]);

    const lastUpdatedMessage = useMemo(() => {
        if (!lastUpdated) {
            return null;
        }
        const formatted = formatDateTime(lastUpdated);
        if (!formatted) {
            return null;
        }
        return t('discoverPage.overlay.lastUpdated', { timestamp: formatted });
    }, [formatDateTime, lastUpdated, t]);

    useEffect(() => {
        if (!creationNotice) {
            return undefined;
        }
        if (typeof window === 'undefined') {
            return undefined;
        }
        const timer = window.setTimeout(() => {
            setCreationNotice(null);
        }, 4000);
        return () => {
            window.clearTimeout(timer);
        };
    }, [creationNotice]);

    const cancelComposerLocationResolution = useCallback(() => {
        composerLocationRequestIdRef.current += 1;
        setIsComposerLocationResolving(false);
    }, []);

    const applyComposerGeocodedLocation = useCallback((location, options = {}) => {
        if (!location) {
            return;
        }

        setComposerMapAddress(location);
        const retentionBaselineValues = options.retentionBaselineValues
            ?? composerLocationRetentionBaselineRef.current;
        const nextLocationUpdate = buildComposerGeocodedLocationUpdate({
            location,
            currentValues: composerValuesRef.current,
            currentSources: composerLocationFieldSourcesRef.current,
            retentionBaselineValues,
        });
        composerLocationFieldSourcesRef.current = nextLocationUpdate.sources;
        setComposerValuesAndTrack({
            ...composerValuesRef.current,
            latitude: formatComposerCoordinate(location.latitude),
            longitude: formatComposerCoordinate(location.longitude),
            ...nextLocationUpdate.values,
        });
        setComposerAddressValue(nextLocationUpdate.addressInputValue, { suppressLookup: true });
        const shouldPreserveRetentionBaseline = nextLocationUpdate.hasRetainedFields
            && Boolean(retentionBaselineValues);
        composerLocationRetentionBaselineRef.current = shouldPreserveRetentionBaseline
            ? retentionBaselineValues
            : buildComposerLocationRetentionBaseline({
                ...nextLocationUpdate.values,
                latitude: location.latitude,
                longitude: location.longitude,
            });
        composerAddressNeedsResolutionRef.current = false;
        setComposerError('');
        setComposerLocationError('');
    }, [setComposerAddressValue, setComposerValuesAndTrack]);

    const handleEntityAddressSelected = useCallback((address) => {
        if (!address) return;
        cancelComposerLocationResolution();
        applyComposerGeocodedLocation(address);
    }, [applyComposerGeocodedLocation, cancelComposerLocationResolution]);

    // Click-outside, keyboard nav, and blur-closing all live inside
    // `useAddressAutocompletePicker`. See hook call above.

    const handleComposerFieldChange = useCallback((field) => (event) => {
        const value = event && typeof event === 'object' && 'target' in event
            ? event.target.value
            : event;
        setComposerError('');
        setComposerValuesAndTrack({
            ...composerValuesRef.current,
            [field]: value,
        });
    }, [setComposerValuesAndTrack]);

    const handleComposerAddressChange = useCallback((event) => {
        const value = event.target.value;
        cancelComposerLocationResolution();
        setComposerError('');
        setComposerLocationError('');
        composerAddressNeedsResolutionRef.current = Boolean(value.trim());
        composerLocationFieldSourcesRef.current = {
            ...createComposerLocationFieldSources(),
            address: resolveComposerLocationSource(value, COMPOSER_MANUAL_LOCATION_SOURCE),
        };
        composerLocationRetentionBaselineRef.current = null;
        setComposerAddressValue(value);
        setComposerValuesAndTrack({
            ...composerValuesRef.current,
            latitude: '',
            longitude: '',
            city: '',
            stateOrRegion: '',
            country: '',
            countryCode: '',
            address: value,
        });
    }, [cancelComposerLocationResolution, setComposerAddressValue, setComposerValuesAndTrack]);

    // Keep the ref pointing at the latest apply-geocoded closure wrapped
    // with cancel-before-apply so in-flight map reverse-geocodes don't
    // race a user-picked suggestion. The picker hook's onSelect binds once
    // at init and dereferences this ref on every pick.
    useEffect(() => {
        applyComposerGeocodedLocationRef.current = (geocoded) => {
            cancelComposerLocationResolution();
            applyComposerGeocodedLocation(geocoded);
        };
    }, [applyComposerGeocodedLocation, cancelComposerLocationResolution]);

    const handleOpenComposer = useCallback(() => {
        if (isComposerSubmitting) return;
        setComposerMapAddress(null);
        const fallbackTypes = composerMarkerTypes.length ? composerMarkerTypes : COMPOSER_MARKER_TYPES;
        cancelPendingEditIntent();
        cancelComposerLocationResolution();
        setEditIntentError('');
        setComposerError('');
        setComposerLocationError('');
        setEditingMarker(null);
        setComposerValuesAndTrack({
            ...composerValuesRef.current,
            type: normalizeTypeForSelect(composerValuesRef.current.type, fallbackTypes),
            privacy: 'PUBLIC',
        });
        setIsComposerOpen(true);
    }, [cancelComposerLocationResolution, cancelPendingEditIntent, composerMarkerTypes, isComposerSubmitting, setComposerValuesAndTrack]);

    const handleCloseComposer = useCallback(() => {
        cancelPendingEditIntent();
        cancelComposerLocationResolution();
        closeComposerAddressDropdown();
        setIsComposerOpen(false);
        setEditIntentError('');
        setComposerError('');
        setComposerLocationError('');
        setEditingMarker(null);
    }, [cancelComposerLocationResolution, cancelPendingEditIntent, closeComposerAddressDropdown]);

    const handleMapClick = useCallback(async (coordinates) => {
        if (!isComposerOpen || isComposerSubmitting) {
            return;
        }

        const lat = coordinates?.lat;
        const lng = coordinates?.lng;

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            return;
        }

        const retentionBaselineValues = composerLocationRetentionBaselineRef.current;
        const clickedLocation = { latitude: lat, longitude: lng };
        setComposerMapAddress(null);
        const clearStaleLocationMetadata = () => {
            if (calculateLocationDistanceKm(retentionBaselineValues, clickedLocation)
                <= COMPOSER_METADATA_RETENTION_MAX_DISTANCE_KM) {
                return;
            }

            composerLocationFieldSourcesRef.current = createComposerLocationFieldSources();
            composerLocationRetentionBaselineRef.current = null;
            setComposerValuesAndTrack({
                ...composerValuesRef.current,
                city: '',
                stateOrRegion: '',
                country: '',
                countryCode: '',
                address: '',
            });
            setComposerAddressValue('', { suppressLookup: true });
        };
        const requestId = composerLocationRequestIdRef.current + 1;
        composerLocationRequestIdRef.current = requestId;
        composerAddressNeedsResolutionRef.current = false;
        setComposerError('');
        setComposerLocationError('');
        setIsComposerLocationResolving(true);

        setComposerValuesAndTrack({
            ...composerValuesRef.current,
            latitude: formatComposerCoordinate(lat),
            longitude: formatComposerCoordinate(lng),
        });

        try {
            const { location, failed, error: resolveError } = await resolveLocationFromCoordinates({
                latitude: lat,
                longitude: lng,
                locale,
            });

            if (!mountedRef.current || composerLocationRequestIdRef.current !== requestId || !composerIsOpenRef.current) {
                return;
            }

            if (location) {
                applyComposerGeocodedLocation(location, { retentionBaselineValues });
                return;
            }

            clearStaleLocationMetadata();
            if (failed) {
                if (resolveError) {
                    console.error(resolveError);
                }
                setComposerLocationError(COMPOSER_LOCATION_RESOLUTION_FAILED_MESSAGE);
            } else {
                setComposerLocationError(COMPOSER_LOCATION_MISS_MESSAGE);
            }
        } catch (err) {
            if (!mountedRef.current || composerLocationRequestIdRef.current !== requestId || !composerIsOpenRef.current) {
                return;
            }
            console.error(err);
            clearStaleLocationMetadata();
            setComposerLocationError(COMPOSER_LOCATION_RESOLUTION_FAILED_MESSAGE);
        } finally {
            if (mountedRef.current && composerLocationRequestIdRef.current === requestId) {
                setIsComposerLocationResolving(false);
            }
        }
    }, [applyComposerGeocodedLocation, isComposerOpen, isComposerSubmitting, locale, setComposerAddressValue, setComposerValuesAndTrack]);

    const handleSelectionChange = useCallback((selection) => {
        const nextIds = (selection?.ids ?? []).map((id) => String(id));
        const nextMarkers = selection?.markers ?? [];
        setSelectedMarkerIds(nextIds);
        setSelectedMarkers(nextMarkers);
        selectedMarkersBucketRef.current = nextMarkers;
    }, []);

    const handleClearSelection = useCallback(() => {
        handleSelectionChange({ ids: [], markers: [] });
    }, [handleSelectionChange]);

    const handleToggleRectangleMode = useCallback(() => {
        setSelectionMode((current) => (current === 'rectangle' ? 'none' : 'rectangle'));
    }, []);

    const handleToggleLassoMode = useCallback(() => {
        setSelectionMode((current) => (current === 'lasso' ? 'none' : 'lasso'));
    }, []);

    const handleCloseSelectionTools = useCallback(() => {
        if (selectionMode === 'rectangle') {
            handleToggleRectangleMode();
            return;
        }
        if (selectionMode === 'lasso') {
            handleToggleLassoMode();
        }
    }, [handleToggleLassoMode, handleToggleRectangleMode, selectionMode]);

    const handleRefreshTable = useCallback(async () => {
        if (!hasActiveMarkerLayerScope) {
            setTableError('');
            setIsTableLoading(false);
            return;
        }
        setTableError('');
        setIsTableLoading(true);
        try {
            await refresh();
        } catch (err) {
            console.error('Failed to load markers for table view', err);
            setTableError(t('discoverPage.table.error', 'We could not load markers right now.'));
        } finally {
            setIsTableLoading(false);
        }
    }, [hasActiveMarkerLayerScope, refresh, t]);

    const handleOpenTable = useCallback(() => {
        if (isTableOpen) {
            return;
        }
        setTableError('');
        setIsTableLoading(hasActiveMarkerLayerScope);
        setIsTableOpen(true);
    }, [hasActiveMarkerLayerScope, isTableOpen]);

    const handleCloseTable = useCallback(() => {
        setIsTableOpen(false);
        setTableError('');
        setIsTableLoading(false);
    }, []);

    const handleToggleTable = useCallback(() => {
        if (isTableOpen) {
            handleCloseTable();
            return;
        }
        handleOpenTable();
    }, [handleCloseTable, handleOpenTable, isTableOpen]);

    const handleEditMarker = useCallback((marker) => {
        // Replacing the composer while an embedded domain form is submitting
        // would unmount it before its completion callback can register the
        // saved canonical object in Discover.
        if (isComposerSubmitting || !marker) {
            return;
        }

        const normalizedType = normalizeMarkerType(marker.originalType) || normalizeMarkerType(marker.type);
        if (normalizedType === 'ARTIST' || normalizedType === 'BAND') {
            return;
        }

        const fallbackTypes = composerMarkerTypes.length ? composerMarkerTypes : COMPOSER_MARKER_TYPES;
        const lat = Number.isFinite(marker.coordinate?.lat) ? marker.coordinate.lat : DISCOVER_FALLBACK_CENTER.lat;
        const lng = Number.isFinite(marker.coordinate?.lng) ? marker.coordinate.lng : DISCOVER_FALLBACK_CENTER.lng;
        const normalizedSelectType = normalizeTypeForSelect(normalizedType, fallbackTypes);
        const nextEditingMarker = {
            ...marker,
            type: normalizedSelectType,
            originalType: normalizedSelectType,
            metadata: marker.metadata && typeof marker.metadata === 'object' && !Array.isArray(marker.metadata)
                ? { ...marker.metadata }
                : {},
        };
        const nextAddress = readMetadataValue(marker.metadata, 'address');

        cancelPendingEditIntent();
        cancelComposerLocationResolution();
        const nextComposerValues = {
            ...composerValuesRef.current,
            ...DEFAULT_COMPOSER_VALUES,
            type: normalizedSelectType,
            name: typeof nextEditingMarker.name === 'string' ? nextEditingMarker.name.trim() : '',
            latitude: formatComposerCoordinate(lat),
            longitude: formatComposerCoordinate(lng),
            city: readMetadataValue(nextEditingMarker.metadata, 'city'),
            stateOrRegion: readMetadataValue(nextEditingMarker.metadata, 'stateOrRegion'),
            country: readMetadataValue(nextEditingMarker.metadata, 'country'),
            countryCode: readMetadataValue(nextEditingMarker.metadata, 'countryCode'),
            address: nextAddress,
            description: readMetadataValue(nextEditingMarker.metadata, 'description'),
            startDate: normalizeDateInputValue(readMetadataValue(nextEditingMarker.metadata, 'startDate')),
            endDate: normalizeDateInputValue(readMetadataValue(nextEditingMarker.metadata, 'endDate')),
            privacy: normalizePrivacy(nextEditingMarker.privacy),
        };
        setComposerValuesAndTrack(nextComposerValues);
        composerLocationFieldSourcesRef.current = buildManualComposerLocationFieldSources({
            city: nextComposerValues.city,
            stateOrRegion: nextComposerValues.stateOrRegion,
            country: nextComposerValues.country,
            countryCode: nextComposerValues.countryCode,
            address: nextComposerValues.address,
        });
        composerLocationRetentionBaselineRef.current = buildComposerLocationRetentionBaseline(nextComposerValues);
        setEditIntentError('');
        setComposerError('');
        setComposerLocationError('');
        setComposerAddressValue(nextAddress, { suppressLookup: true });
        composerAddressNeedsResolutionRef.current = false;
        setEditingMarker(nextEditingMarker);
        setIsComposerOpen(true);
    }, [
        cancelComposerLocationResolution,
        cancelPendingEditIntent,
        composerMarkerTypes,
        isComposerSubmitting,
        setComposerAddressValue,
        setComposerValuesAndTrack,
    ]);

    useEffect(() => {
        if (!discoverEditIntent) {
            return;
        }

        setEditIntentError('');
        const requestId = editIntentRequestIdRef.current + 1;
        editIntentRequestIdRef.current = requestId;
        setPendingEditIntent({
            ...discoverEditIntent,
            requestId,
        });

        const nextState = location.state && typeof location.state === 'object'
            ? { ...location.state }
            : {};
        delete nextState.discoverComposerIntent;
        navigate(
            `${location.pathname}${location.search}`,
            {
                replace: true,
                state: Object.keys(nextState).length > 0 ? nextState : null,
            },
        );

    }, [
        discoverEditIntent,
        location.pathname,
        location.search,
        location.state,
        navigate,
    ]);

    useEffect(() => {
        if (!pendingEditIntent) {
            return;
        }

        const requestId = pendingEditIntent.requestId;
        let isCancelled = false;
        const loadEditIntent = async () => {
            try {
                const entity = await eventService.getEntity(
                    pendingEditIntent.objectType,
                    pendingEditIntent.objectId,
                );
                if (isCancelled || editIntentRequestIdRef.current !== requestId) {
                    return;
                }
                const marker = toComposerEditMarker(entity);
                if (!marker) {
                    throw new Error('Unable to normalize discover edit intent entity');
                }
                handleEditMarker(marker);
            } catch (error) {
                if (!isCancelled && editIntentRequestIdRef.current === requestId) {
                    console.error('Failed to hydrate discover edit intent', error);
                    setEditIntentError(t(
                        'discoverPage.composer.intentLoadError',
                        'We could not load that item for editing right now.',
                    ));
                }
            } finally {
                if (!isCancelled && editIntentRequestIdRef.current === requestId) {
                    setPendingEditIntent(null);
                }
            }
        };

        void loadEditIntent();

        return () => {
            isCancelled = true;
        };
    }, [
        pendingEditIntent,
        handleEditMarker,
        t,
    ]);

    const handleOpenScheduledDetails = useCallback((marker) => {
        const seed = toScheduledDetailSeed(marker);
        if (!seed) {
            return;
        }
        setScheduledDetailTarget(seed);
    }, []);

    const handleCloseScheduledDetails = useCallback(() => {
        setScheduledDetailTarget(null);
    }, []);

    const resolveTypedComposerAddress = useCallback(async () => {
        const currentValues = composerValuesRef.current;
        const typedAddress = currentValues.address.trim();
        if (!composerAddressNeedsResolutionRef.current || !typedAddress) {
            return currentValues;
        }

        const requestId = composerLocationRequestIdRef.current + 1;
        composerLocationRequestIdRef.current = requestId;
        setComposerLocationError('');
        setIsComposerLocationResolving(true);

        try {
            const resolution = await resolveTypedAddress({
                text: typedAddress,
                countryCode: normalizeCountryCode(currentValues.countryCode)
                    || DEFAULT_LOCATION_COUNTRY_OPTION.code,
                limit: 1,
            });

            if (!mountedRef.current || composerLocationRequestIdRef.current !== requestId || !composerIsOpenRef.current) {
                return null;
            }

            if (resolution.status !== TYPED_ADDRESS_RESOLUTION_STATUS.RESOLVED) {
                setComposerLocationError(
                    resolution.status === TYPED_ADDRESS_RESOLUTION_STATUS.COARSE_MATCH
                        ? COMPOSER_COARSE_ADDRESS_MATCH_MESSAGE
                        : COMPOSER_ADDRESS_RESOLUTION_FAILED_MESSAGE,
                );
                return null;
            }

            applyComposerGeocodedLocation(resolution.location);
            return composerValuesRef.current;
        } catch (error) {
            if (!mountedRef.current || composerLocationRequestIdRef.current !== requestId || !composerIsOpenRef.current) {
                return null;
            }
            console.error('Failed to resolve composer address:', error);
            setComposerLocationError(COMPOSER_ADDRESS_RESOLUTION_FAILED_MESSAGE);
            return null;
        } finally {
            if (mountedRef.current && composerLocationRequestIdRef.current === requestId) {
                setIsComposerLocationResolving(false);
            }
        }
    }, [applyComposerGeocodedLocation]);

    const ensureMarkerLayerEnabled = useCallback(async (savedMarkerType) => {
        // Auto-enable (or auto-create) a layer for the created marker's type.
        // Await loadLayers() first so the updater sees server state, not a stale
        // empty array from before the initial fetch resolved.
        if (savedMarkerType) {
            const freshLayers = await loadLayers();
            if (!freshLayers) {
                // Layer fetch failed or returned stale data — skip auto-create/enable
                // to avoid creating duplicate layers from missing information.
                return;
            }
            const serverLayers = freshLayers;
            const alreadyEnabled = serverLayers.some((l) => l.objectType === savedMarkerType && l.enabled);
            if (!alreadyEnabled) {
                const existing = serverLayers.find((l) => l.objectType === savedMarkerType);
                if (existing) {
                    // Enable the existing layer locally and persist to backend
                    setLayerEnabledOptimistically(existing.id, true);
                    persistLayerEnabled(existing.id, true, 'Failed to persist auto-enabled layer toggle');
                }
            }
            const needsCreate = !alreadyEnabled && !serverLayers.some((l) => l.objectType === savedMarkerType);
            if (needsCreate) {
                const baseName = savedMarkerType.charAt(0) + savedMarkerType.slice(1).toLowerCase();
                // Try the default name first, then suffixed variants to
                // avoid duplicate-name conflicts with existing layers.
                const candidates = [baseName];
                for (let i = 2; i <= 5; i++) candidates.push(`${baseName} (${i})`);

                let created = false;
                for (const candidateName of candidates) {
                    try {
                        const saved = await geoService.createCustomLayer({
                            name: candidateName,
                            objectType: savedMarkerType,
                            filters: null,
                            popup: null,
                        });
                        layerMutationVersionRef.current += 1;
                        setLayers((prev) => [
                            ...prev,
                            {
                                id: saved.id,
                                name: saved.name || candidateName,
                                objectType: saved.type || savedMarkerType,
                                filters: saved.filters || null,
                                popup: saved.popup || null,
                                displayOrder: saved.displayOrder ?? null,
                                color: normalizeHexColor(saved.color, getDefaultTypeColor(savedMarkerType)),
                                enabled: true,
                                createdAt: saved.createdAt || null,
                            },
                        ]);
                        created = true;
                        break;
                    } catch (layerErr) {
                        // If it's a duplicate-name conflict (409), try next name
                        if (layerErr?.response?.status === 409) continue;
                        console.error('Failed to auto-create layer for type', savedMarkerType, layerErr);
                        break;
                    }
                }
                if (!created) {
                    // All name candidates failed — reload layers in case backend
                    // auto-provisioned one we can enable instead
                    const reloaded = await loadLayers();
                    const fallback = (reloaded || []).find((l) => l.objectType === savedMarkerType && !l.enabled);
                    if (fallback) {
                        setLayerEnabledOptimistically(fallback.id, true);
                        persistLayerEnabled(fallback.id, true, 'Failed to persist fallback layer toggle');
                    }
                }
            }
        }
    }, [loadLayers, persistLayerEnabled, setLayerEnabledOptimistically]);

    const handleEntityCreated = useCallback((entity) => {
        registerCreatedEntity(entity);
        setCreationNotice({
            title: t('discoverPage.composer.createdTitle', 'Marker added'),
            message: t('discoverPage.composer.createdMessage', { name: entity.title || entity.name }),
        });
        // Creation has succeeded. Layer failures must not turn it into a failed
        // submit or invite the user to create the same entity again.
        void ensureMarkerLayerEnabled(entity.objectType).catch((error) => {
            console.error('Failed to enable layer for created entity', error);
        });
    }, [ensureMarkerLayerEnabled, registerCreatedEntity, t]);

    const handleComposerSubmit = useCallback(async (submissionEvent) => {
        submissionEvent.preventDefault();
        setComposerError('');

        if (isComposerLocationResolving) {
            setComposerError(COMPOSER_LOCATION_RESOLVING_SUBMIT_MESSAGE);
            return;
        }

        const initialName = composerValuesRef.current.name.trim();
        if (!initialName) {
            setComposerError('A marker name is required.');
            return;
        }

        const resolvedValues = await resolveTypedComposerAddress();
        if (!resolvedValues) {
            return;
        }

        const trimmedName = resolvedValues.name.trim();
        if (!trimmedName) {
            setComposerError('A marker name is required.');
            return;
        }
        const selectedMarkerType = normalizeMarkerType(resolvedValues.type);
        const originalMarkerType = editingMarker
            ? normalizeMarkerType(editingMarker.originalType) || normalizeMarkerType(editingMarker.type)
            : '';
        const markerType = originalMarkerType || selectedMarkerType;

        const latitude = Number.parseFloat(resolvedValues.latitude);
        const longitude = Number.parseFloat(resolvedValues.longitude);

        if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
            setComposerError('Choose an address to set a valid marker position.');
            return;
        }
        if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
            setComposerError('Choose an address to set a valid marker position.');
            return;
        }

        const trimmedCity = resolvedValues.city.trim();
        if (!trimmedCity) {
            setComposerError('Choose a complete address that includes a city.');
            return;
        }
        const trimmedStateOrRegion = resolvedValues.stateOrRegion.trim();

        const trimmedCountry = resolvedValues.country.trim();
        if (!trimmedCountry) {
            setComposerError('Choose a complete address that includes a country.');
            return;
        }

        const trimmedAddress = resolvedValues.address.trim();
        if (markerType === 'VENUE' && !trimmedAddress) {
            setComposerError('Address is required for venues.');
            return;
        }

        if (isScheduledMarkerType(markerType) && !resolvedValues.startDate) {
            setComposerError('Start date is required for events and sessions.');
            return;
        }

        const metadata = editingMarker
            && editingMarker.metadata
            && typeof editingMarker.metadata === 'object'
            && !Array.isArray(editingMarker.metadata)
            ? { ...editingMarker.metadata }
            : {};
        if (trimmedCity) {
            metadata.city = trimmedCity;
        } else {
            delete metadata.city;
        }
        if (trimmedStateOrRegion) {
            metadata.stateOrRegion = trimmedStateOrRegion;
        } else {
            delete metadata.stateOrRegion;
        }
        if (trimmedCountry) {
            metadata.country = trimmedCountry;
        } else {
            delete metadata.country;
        }
        const trimmedCountryCode = resolvedValues.countryCode.trim().toUpperCase();
        if (trimmedCountryCode) {
            metadata.countryCode = trimmedCountryCode;
        } else {
            delete metadata.countryCode;
        }
        if (trimmedAddress) {
            metadata.address = trimmedAddress;
        } else {
            delete metadata.address;
        }
        const trimmedDescription = resolvedValues.description.trim();
        if (trimmedDescription) {
            metadata.description = trimmedDescription;
        } else {
            delete metadata.description;
        }
        if (resolvedValues.startDate) {
            metadata.startDate = resolvedValues.startDate;
        } else {
            delete metadata.startDate;
        }
        if (resolvedValues.endDate) {
            metadata.endDate = resolvedValues.endDate;
        } else {
            delete metadata.endDate;
        }
        if (!isScheduledMarkerType(markerType)) {
            delete metadata.startDate;
            delete metadata.endDate;
            delete metadata.status;
        }
        if (authorName) {
            metadata.authorName = authorName;
        }

        setIsComposerSubmitting(true);
        try {
            const fallbackTypes = composerMarkerTypes.length ? composerMarkerTypes : COMPOSER_MARKER_TYPES;
            const payload = {
                name: trimmedName,
                type: normalizeTypeForSelect(markerType, fallbackTypes),
                privacy: normalizePrivacy(resolvedValues.privacy),
                coordinate: {
                    latitude,
                    longitude,
                },
                metadata,
            };

            if (editingMarker) {
                await updateMarker(editingMarker.id, payload);
            } else {
                await createMarker(payload);
            }

            setCreationNotice({
                title: editingMarker
                    ? t('discoverPage.composer.updatedTitle', 'Marker updated')
                    : t('discoverPage.composer.createdTitle', 'Marker added'),
                message: editingMarker
                    ? t('discoverPage.composer.updatedMessage', { name: trimmedName })
                    : t('discoverPage.composer.createdMessage', { name: trimmedName }),
            });

            setComposerValues((prev) => ({
                ...prev,
                name: '',
                description: '',
            }));
            composerAddressNeedsResolutionRef.current = false;
            setEditingMarker(null);
            setIsComposerOpen(false);
            await ensureMarkerLayerEnabled(markerType);
        } catch (err) {
            console.error('Failed to save map marker:', err);
            setComposerError('We could not save your marker. Please try again shortly.');
        } finally {
            setIsComposerSubmitting(false);
        }
    }, [
        authorName,
        createMarker,
        editingMarker,
        composerMarkerTypes,
        isComposerLocationResolving,
        resolveTypedComposerAddress,
        t,
        updateMarker,
        ensureMarkerLayerEnabled,
    ]);

    const handleAddLayer = useCallback(() => {
        setIsLayerWizardOpen(true);
    }, []);

    const handleCloseLayerWizard = useCallback(() => {
        setIsLayerWizardOpen(false);
        setEditingLayer(null);
    }, []);

    /** Create a new custom layer by persisting to the backend. */
    const handleCreateLayer = useCallback(async (payload) => {
        try {
            const saved = await geoService.createCustomLayer({
                name: payload.name,
                objectType: payload.objectType,
                filters: payload.filters || null,
                popup: payload.popup || null,
            });
            layerMutationVersionRef.current += 1;
            setLayers((prev) => [
                ...prev,
                {
                    id: saved.id,
                    name: saved.name || payload.name,
                    objectType: saved.type || payload.objectType,
                    filters: saved.filters || null,
                    popup: saved.popup || null,
                    displayOrder: saved.displayOrder ?? null,
                    color: saved.color || null,
                    enabled: typeof saved.enabled === 'boolean' ? saved.enabled : true,
                    createdAt: saved.createdAt || null,
                },
            ]);
        } catch (err) {
            console.error('Failed to create custom layer', err);
            throw err;
        }
    }, []);

    const handleDeleteLayer = useCallback((layerId) => {
        setDeletingLayerId(layerId);
    }, []);

    const handleConfirmDeleteLayer = useCallback(async () => {
        const layerId = deletingLayerId;
        setDeletingLayerId(null);
        if (!layerId) return;
        try {
            await geoService.deleteLayer(layerId);
            layerMutationVersionRef.current += 1;
            setLayers((prev) => prev.filter((l) => l.id !== layerId));
        } catch (err) {
            console.error('Failed to delete custom layer', err);
        }
    }, [deletingLayerId]);

    const handleCancelDeleteLayer = useCallback(() => {
        setDeletingLayerId(null);
    }, []);

    const handleEditLayer = useCallback((layerId) => {
        const layer = layers.find((l) => l.id === layerId);
        if (layer) {
            setEditingLayer(layer);
            setIsLayerWizardOpen(true);
        }
    }, [layers]);

    const handleUpdateLayer = useCallback(async (payload) => {
        try {
            const { id, ...rest } = payload;
            const saved = await geoService.updateLayer(id, {
                name: rest.name,
                filters: rest.filters || null,
                popup: rest.popup || null,
            });
            layerMutationVersionRef.current += 1;
            setLayers((prev) => prev.map((l) => {
                if (l.id !== id) return l;
                return {
                    ...l,
                    name: saved.name || rest.name,
                    filters: saved.filters || null,
                    popup: saved.popup || null,
                    displayOrder: saved.displayOrder ?? l.displayOrder,
                    color: saved.color ?? l.color,
                };
            }));
        } catch (err) {
            console.error('Failed to update custom layer', err);
            throw err;
        }
    }, []);

    const flushPendingToggles = useCallback(() => {
        if (layerToggleTimerRef.current) {
            clearTimeout(layerToggleTimerRef.current);
            layerToggleTimerRef.current = null;
        }
        const pending = { ...pendingLayerTogglesRef.current };
        pendingLayerTogglesRef.current = {};
        if (Object.keys(pending).length === 0) return;

        Object.entries(pending).forEach(([layerId, enabled]) => {
            persistLayerEnabled(layerId, enabled, 'Failed to persist custom layer toggle');
        });
    }, [persistLayerEnabled]);

    const handleToggleLayer = useCallback((layerId, checked) => {
        pendingLayerTogglesRef.current[layerId] = checked;
        setLayerEnabledOptimistically(layerId, checked);

        if (layerToggleTimerRef.current) {
            clearTimeout(layerToggleTimerRef.current);
        }

        layerToggleTimerRef.current = setTimeout(() => flushPendingToggles(), 150);
    }, [flushPendingToggles, setLayerEnabledOptimistically]);

    useEffect(() => () => {
        flushPendingToggles();
        if (layerRetryTimerRef.current) {
            clearTimeout(layerRetryTimerRef.current);
            layerRetryTimerRef.current = null;
        }
    }, [flushPendingToggles]);

    const handleLayerColorChange = useCallback((layerId, color) => {
        const resolvedColor = normalizeHexColor(color, '#5f8cff');
        layerMutationVersionRef.current += 1;
        setLayers((prev) => prev.map((l) => (l.id === layerId ? { ...l, color: resolvedColor } : l)));
        geoService.updateLayer(layerId, { color: resolvedColor }).catch((err) => {
            console.error('Failed to persist custom layer color', err);
        });
    }, []);

    const reorderTimerRef = useRef(null);
    const reorderAbortRef = useRef(null);
    const latestLayerIdsRef = useRef([]);
    const handleReorderLayers = useCallback((oldIndex, newIndex) => {
        setLayers((prev) => {
            const reordered = arrayMove(prev, oldIndex, newIndex)
                .map((l, i) => ({ ...l, displayOrder: i }));

            // Keep ref in sync so unmount cleanup can read it directly
            latestLayerIdsRef.current = reordered.map((l) => l.id);

            // Debounce: cancel any pending flush and schedule a new one
            clearTimeout(reorderTimerRef.current);
            if (reorderAbortRef.current) {
                reorderAbortRef.current.abort();
            }
            reorderTimerRef.current = setTimeout(() => {
                reorderTimerRef.current = null;
                const controller = new AbortController();
                reorderAbortRef.current = controller;
                geoService.reorderLayers(latestLayerIdsRef.current, { signal: controller.signal }).catch((err) => {
                    if (err?.name !== 'AbortError') {
                        console.error('Failed to persist layer reorder', err);
                    }
                });
            }, 300);

            return reordered;
        });
        layerMutationVersionRef.current += 1;
    }, []);

    // Flush pending reorder on unmount so the order is not lost
    useEffect(() => () => {
        if (reorderTimerRef.current) {
            clearTimeout(reorderTimerRef.current);
            reorderTimerRef.current = null;
            // Read latest IDs from ref — setLayers updaters are dropped during unmount
            geoService.reorderLayers(latestLayerIdsRef.current).catch(() => {});
        }
    }, []);

    const existingLayerNames = useMemo(
        () => layers
            .filter((l) => !editingLayer || l.id !== editingLayer.id)
            .map((l) => l.name),
        [layers, editingLayer],
    );

    useEffect(() => {
        setComposerValues((prev) => ({
            ...prev,
            type: normalizeTypeForSelect(prev.type, composerMarkerTypes.length ? composerMarkerTypes : COMPOSER_MARKER_TYPES),
        }));
    }, [composerMarkerTypes]);

    const showComposerAddressNoResults = isComposerAddressOpen
        && !isComposerAddressLoading
        && !composerAddressLookupError
        && composerAddressDebouncedValue.trim().length >= 3
        && composerAddressSuggestions.length === 0;

    const composerCard = isComposerOpen ? (
        <DiscoverMarkerComposerCard
            t={t}
            isEditing={isEditing}
            isTypeLocked={isEditing}
            isSubmitting={isComposerSubmitting}
            isLocationResolving={isComposerLocationResolving}
            isSaveDisabled={isComposerSubmitting || isComposerLocationResolving}
            values={composerValues}
            markerTypes={composerMarkerTypes.length ? composerMarkerTypes : COMPOSER_MARKER_TYPES}
            errorMessage={composerError}
            addressValue={composerAddressValue}
            addressFieldRef={composerAddressFieldRef}
            addressSuggestions={composerAddressSuggestions}
            addressHighlightedIndex={composerAddressHighlightedIndex}
            isAddressOpen={isComposerAddressOpen}
            isAddressLoading={isComposerAddressLoading}
            showAddressNoResults={showComposerAddressNoResults}
            addressLookupError={composerAddressLookupError}
            locationError={composerLocationError}
            onClose={handleCloseComposer}
            onSubmit={handleComposerSubmit}
            onFieldChange={handleComposerFieldChange}
            onAddressChange={handleComposerAddressChange}
            onAddressKeyDown={handleComposerAddressKeyDown}
            onAddressFieldBlur={handleComposerAddressFieldBlur}
            onOpenAddressDropdown={openComposerAddressDropdown}
            onAddressSuggestionSelect={handleComposerAddressSuggestionSelect}
            onAddressSuggestionHighlight={setComposerAddressHighlightedIndex}
        >
            {!isEditing && composerValues.type === 'EVENT' ? (
                <EventCreateModal
                    isOpen
                    embedded
                    mapAddress={composerMapAddress}
                    onAddressSelected={handleEntityAddressSelected}
                    locationPending={isComposerLocationResolving}
                    onBusyChange={setIsComposerSubmitting}
                    onCreated={handleEntityCreated}
                    onClose={handleCloseComposer}
                />
            ) : !isEditing && composerValues.type === 'VENUE' ? (
                <VenueFormModal
                    isOpen
                    embedded
                    mapAddress={composerMapAddress}
                    onAddressSelected={handleEntityAddressSelected}
                    locationPending={isComposerLocationResolving}
                    onBusyChange={setIsComposerSubmitting}
                    onSaved={handleEntityCreated}
                    onClose={handleCloseComposer}
                />
            ) : null}
        </DiscoverMarkerComposerCard>
    ) : null;

    return (
        <div id="discover-page">
            <OpenStreetMapView
                containerRef={mapContainerRef}
                className={isSelectionPaneOpen ? 'openstreet-map-view--selection-pane-open' : ''}
                center={discoverViewport.center}
                zoom={discoverViewport.zoom}
                minZoom={DISCOVER_FALLBACK_ZOOM}
                markers={markersWithPopupOverrides}
                markerDataKey={markerDataKey}
                viewportKey={viewportKey}
                preserveOpenPopupOnDataGap={preserveOpenPopupOnDataGap}
                currentUserId={currentUser?.id}
                currentUsername={currentUser?.username}
                markerColors={markerColors}
                cameraRequest={searchCameraRequest}
                searchResultMarker={searchResultMarker}
                overlay={(
                    <>
                        <div className="discover-overlay">
                            <DiscoverMapPanel
                                t={t}
                                layers={layers}
                                layerCounts={layerCounts}
                                onToggleLayer={handleToggleLayer}
                                onEditLayer={handleEditLayer}
                                onDeleteLayer={handleDeleteLayer}
                                onLayerColorChange={handleLayerColorChange}
                                onReorderLayers={handleReorderLayers}
                                onAddMarkerClick={handleOpenComposer}
                                onAddLayerClick={handleAddLayer}
                                totalCount={visibleMarkerCount}
                                selectedCount={selectedCount}
                                lastUpdatedMessage={lastUpdatedMessage}
                                onRefresh={hasActiveMarkerLayerScope ? handleManualRefresh : undefined}
                                isComposerOpen={isComposerOpen}
                                composerCard={composerCard}
                            />
                        </div>
                        <DiscoverOverlayStack ariaLabel={t('discoverPage.overlay.statusAria', 'Map status updates')}>
                            {statusBanner}
                            {creationNotice && (
                                <MapStatusBanner
                                    variant="info"
                                    title={creationNotice.title}
                                    message={creationNotice.message}
                                    actionLabel="Dismiss"
                                    onAction={() => setCreationNotice(null)}
                                />
                            )}
                            {editIntentError && (
                                <MapStatusBanner
                                    variant="error"
                                    title={editIntentError}
                                    actionLabel="Dismiss"
                                    onAction={() => setEditIntentError('')}
                                />
                            )}
                        </DiscoverOverlayStack>
                    </>
                )}
                loadingLabel={loadingLabel}
                onMapClick={handleMapClick}
                onEditMarker={handleEditMarker}
                onOpenMarkerDetails={handleOpenScheduledDetails}
                selectionMode={selectionMode}
                onSelectionChange={handleSelectionChange}
                selectedMarkerIds={selectedMarkerIds}
                onViewportChange={handleViewportChange}
            >
                <BottomSheet
                    id="discover-selection-table"
                    className="discover-selection-table"
                    panelClassName="discover-selection-table__panel"
                    contentClassName="discover-selection-table__content"
                    isOpen={isTableOpen}
                    isResizing={isSelectionPaneResizing}
                    height={selectionPaneHeight}
                    handleAriaLabel={t('discoverPage.selection.resizeAria', 'Resize selection panel')}
                    handleAriaControls="discover-selection-table"
                    onHandlePointerDown={startSelectionPaneResize}
                    onHandleKeyDown={handleSelectionPaneResizerKeyDown}
                >
                    <DiscoverMarkerTablePanel
                        t={t}
                        isOpen={isTableOpen}
                        markers={markersWithPopupOverrides}
                        markerColors={markerColors}
                        selectedIds={selectedMarkerIds}
                        onSelectionChange={handleSelectionChange}
                        onRefresh={handleRefreshTable}
                        isLoading={hasActiveMarkerLayerScope && (isTableLoading || isLoading)}
                        onClose={handleCloseTable}
                        formatDateTime={formatDateTime}
                        errorMessage={tableError}
                        onOpenScheduledDetails={handleOpenScheduledDetails}
                    />
                </BottomSheet>
                <div className="discover-selection-pane">
                    <div className="discover-selection-pane__stack">
                        <div className="discover-selection-pane__group">
                            <SelectionToolPanel
                                t={t}
                                selectionMode={selectionMode}
                                onClearSelection={handleClearSelection}
                                onClosePanel={handleCloseSelectionTools}
                                selectedCount={selectedCount}
                            />
                        </div>
                        {(() => {
                            const toolbarColumn = (
                                <div
                                    className={`discover-selection-pane__toolbar-column${mobileHeaderSlot ? ' discover-selection-pane__toolbar-column--in-header' : ''}`}
                                >
                                    <DiscoverMapSearchControl
                                        t={t}
                                        value={searchAddressValue}
                                        onChange={handleSearchAddressChange}
                                        onSelect={handleSearchAddressSelect}
                                    />
                                    <DiscoverSelectionActionsToolbar
                                        t={t}
                                        selectionMode={selectionMode}
                                        isTableOpen={isTableOpen}
                                        onSelectRectangle={handleToggleRectangleMode}
                                        onSelectLasso={handleToggleLassoMode}
                                        onOpenTable={handleToggleTable}
                                    />
                                </div>
                            );
                            return mobileHeaderSlot
                                ? createPortal(toolbarColumn, mobileHeaderSlot)
                                : toolbarColumn;
                        })()}
                    </div>
                </div>
            </OpenStreetMapView>
            <DiscoverLayerWizardModal
                isOpen={isLayerWizardOpen}
                onClose={handleCloseLayerWizard}
                onCreateLayer={handleCreateLayer}
                onUpdateLayer={handleUpdateLayer}
                editingLayer={editingLayer}
                existingLayerNames={existingLayerNames}
            />
            <ScheduledEntityDetailModal
                isOpen={Boolean(scheduledDetailTarget)}
                objectType={scheduledDetailTarget?.objectType || null}
                objectId={scheduledDetailTarget?.objectId || null}
                initialSummary={scheduledDetailTarget?.summary || null}
                initialAttendance={null}
                onClose={handleCloseScheduledDetails}
            />
            <ConfirmDialog
                isOpen={Boolean(deletingLayerId)}
                title={t('discoverPage.panel.deleteLayerTitle', 'Delete layer?')}
                message={t('discoverPage.panel.deleteLayerConfirm', 'This layer will be permanently removed.')}
                confirmLabel={t('discoverPage.panel.deleteLayer', 'Delete')}
                cancelLabel={t('discoverPage.layerWizard.actions.cancel', 'Cancel')}
                onConfirm={handleConfirmDeleteLayer}
                onCancel={handleCancelDeleteLayer}
                confirmVariant="danger"
            />
        </div>
    );
}
